/**
 * The drawable half of `polygon-outlines.ts`: one instanced screen-space-width
 * line mesh for a whole region's terrain edges.
 *
 * **Why a fat-line material and not `THREE.LineSegments`.** The width has to
 * be *screen-space*, because that is the property being reproduced: the
 * original's rasterizer floor is one pixel of a 320-wide framebuffer at every
 * distance, so a road subtends a fixed fraction of the field of view whether
 * it is 200 m or 12 km away. A plain `LineSegments` draws exactly one device
 * pixel, which in a 3,200-CSS-pixel-wide view is a tenth of that and reads as a
 * hairline scratch rather than a road.
 *
 * **Why the geometry is assembled here rather than with the `LineSegments2`
 * addon.** `three/examples/jsm/lines/LineSegmentsGeometry.js` imports from
 * `"three"`, which would pull a second copy of three's core into `/fly`'s
 * bundle alongside the `three/webgpu` build the whole app already uses. The
 * layout it produces is small and fully documented — a unit quad strip plus
 * two interleaved per-instance endpoint attributes — so it is built here from
 * `three/webgpu`'s own classes instead. `Line2NodeMaterial` reads nothing off
 * the object beyond those attributes (it takes the viewport from the renderer
 * itself), so a plain `Mesh` is all it needs.
 */

import * as THREE from "three/webgpu";
import { originalPixelWidth, type SegmentBuffers } from "./polygon-outlines.ts";

/** The unit quad `Line2NodeMaterial`'s vertex stage expands into a screen-space-thick segment (the layout `LineSegmentsGeometry` defines). */
const QUAD_POSITIONS = [-1, 2, 0, 1, 2, 0, -1, 1, 0, 1, 1, 0, -1, 0, 0, 1, 0, 0, -1, -1, 0, 1, -1, 0];
const QUAD_UVS = [-1, 2, 1, 2, -1, 1, 1, 1, -1, -1, 1, -1, -1, -2, 1, -2];
const QUAD_INDEX = [0, 2, 1, 2, 3, 1, 2, 4, 3, 4, 5, 3, 4, 6, 5, 6, 7, 5];
const FLOATS_PER_SEGMENT = 6;

function boundingSphereOf(positions: Float32Array): THREE.Sphere {
  const box = new THREE.Box3();
  const point = new THREE.Vector3();
  for (let at = 0; at < positions.length; at += 3) {
    box.expandByPoint(point.set(positions[at]!, positions[at + 1]!, positions[at + 2]!));
  }
  return box.getBoundingSphere(new THREE.Sphere());
}

export interface Hairlines {
  readonly mesh: THREE.Mesh;
  /** Re-derives the line width after a resize, so a road keeps its one-original-pixel share of the view. */
  readonly setViewWidth: (viewWidthCssPx: number) => void;
}

/**
 * Builds the line mesh for `buffers`, or `undefined` when there is nothing to
 * draw. `viewWidthCssPx` is the view's width in **CSS pixels** —
 * `renderer.getSize()`, never `getDrawingBufferSize()`; see
 * `originalPixelWidth` for why the pixel ratio must not be in this number.
 */
export function createHairlines(buffers: SegmentBuffers, viewWidthCssPx: number): Hairlines | undefined {
  if (buffers.segmentCount === 0) return undefined;

  const geometry = new THREE.InstancedBufferGeometry();
  geometry.setIndex(QUAD_INDEX);
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(QUAD_POSITIONS, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(QUAD_UVS, 2));

  const endpoints = new THREE.InstancedInterleavedBuffer(buffers.positions, FLOATS_PER_SEGMENT, 1);
  geometry.setAttribute("instanceStart", new THREE.InterleavedBufferAttribute(endpoints, 3, 0));
  geometry.setAttribute("instanceEnd", new THREE.InterleavedBufferAttribute(endpoints, 3, 3));

  const endpointColors = new THREE.InstancedInterleavedBuffer(buffers.colors, FLOATS_PER_SEGMENT, 1);
  geometry.setAttribute("instanceColorStart", new THREE.InterleavedBufferAttribute(endpointColors, 3, 0));
  geometry.setAttribute("instanceColorEnd", new THREE.InterleavedBufferAttribute(endpointColors, 3, 3));

  geometry.instanceCount = buffers.segmentCount;
  geometry.boundingSphere = boundingSphereOf(buffers.positions);

  const material = new THREE.Line2NodeMaterial({ vertexColors: true });
  material.worldUnits = false;
  material.linewidth = originalPixelWidth(viewWidthCssPx);

  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;

  return {
    mesh,
    setViewWidth: (widthCssPx: number) => {
      material.linewidth = originalPixelWidth(widthCssPx);
    },
  };
}
